import type {
    AdditionalArticle,
    Article,
    FullPrice,
    NeoArticleClassificationValue,
    PDSArticle,
    PDSPrice,
    Warranties,
} from '@expert/types';

export const hasCrossSaleArticles = (crossSaleArticles: Array<any>) => {
    return crossSaleArticles?.length && crossSaleArticles?.length > 0;
};

export const hasWarranty = (warranty: Warranties) => {
    return warranty?.warrantyList && warranty?.warrantyList?.length > 0;
};

export const hasWarrantyAndAvailability = (warranty: Warranties, price: FullPrice) => {
    const onlineAvailabilityArray = [
        'NOT_AVAILABLE',
        'ONLY_IN_STORE',
        'NONE',
        'SOLD_OUT',
        'ORDER_IN_ADVANCE',
        'PREORDER',
    ];
    const isAvailable = price?.onlineButtonAction && !onlineAvailabilityArray.includes(price?.onlineButtonAction);

    return hasWarranty(warranty) && isAvailable;
};

export const hasWarrantyOrCrossSale = (warranty: Warranties, crossSaleArticles: Array<AdditionalArticle>) => {
    return hasWarranty(warranty) || hasCrossSaleArticles(crossSaleArticles);
};

export const getAdditionalArticles = (articleId: string, storeId: string) => {
    return getRelatedArticles(articleId, storeId, 'ADDITIONAL_ARTICLES');
};

const getRelatedArticles = (articleId: string, storeId: string, relationType: string) => {
    const logger = useLogger(`getRelatedArticles_${relationType}`);
    return useLazyFetch<Map<string, Array<AdditionalArticle>>>(
        useRuntimeConfig().public.apiUrl + '/api/search/related',
        {
            key: `${articleId}_${storeId}_ADDITIONAL`,
            query: {
                articleId,
                storeId,
                relationType,
            },
            server: false,
            transform(data) {
                let dataAsMap: Map<string, Array<AdditionalArticle>> = new Map();
                if (data) {
                    try {
                        dataAsMap = new Map(Object.entries(data));
                    } catch (error) {
                        logger.warn(`Couldn't create map of AdditionalArticles response`, error);
                    }
                }
                return dataAsMap;
            },
        },
    );
};

export const getPdsArticleFromArticle = async (article: Article): Promise<PDSArticle> => {
    const webcode: string = article.webcode;
    let slug: string = article.slug;

    if (slug) {
        slug = slug.replace(/\.html$/, '');
        if (webcode && slug.startsWith(`${webcode}-`)) {
            slug = slug.slice(webcode.length + 1);
        }
    }

    return $fetch<PDSArticle>(`${import.meta.client ? useRuntimeConfig().public.apiUrl : ''}/api/articlepds`, {
        query: {
            webcode: webcode,
            slug: slug,
        },
    });
};

export const getPdsPriceFromArticle = async (article: Article): Promise<PDSPrice> => {
    return await $fetch<PDSPrice>(`${import.meta.client ? useRuntimeConfig().public.apiUrl : ''}/api/pricepds`, {
        query: {
            webcode: article.webcode,
            storeId: useCurrentStoreId(),
        },
    });
};

export const getEnergyEfficiencyData = (article: Article): EnergyEfficiencyData => {
    const technicalData = article?.technicalData ?? [];
    const energyEfficiencyData = {
        label: {
            type: 'DEFAULT',
        },
    } as EnergyEfficiencyData;

    for (const data of technicalData) {
        if (data?.value?.value && data.value.value !== '' && data.value.title === 'energyEfficiency') {
            switch (data.value.description) {
                case 'efficiencyClass':
                    energyEfficiencyData.label.letter = data.value.value?.charAt(0);
                    energyEfficiencyData.label.pluses = data.value.value?.substring(1);
                    break;
                case 'efficiencyRange':
                    energyEfficiencyData.label.letterRange = data.value.value;
                    break;
                case 'colorCode':
                    energyEfficiencyData.label.colorPalette = data.value.value.substring(1).replace('x', '#');
                    break;
                case 'image':
                    energyEfficiencyData.label.imageId = data.value.value;
                    break;
                case 'pdf':
                    energyEfficiencyData.dataFileId = data.value.value;
                    break;
            }
        }
    }

    if (energyEfficiencyData.label.letter?.includes('+') || energyEfficiencyData.label.letterRange?.includes('+')) {
        energyEfficiencyData.label.type = 'LEGACY';
    }

    return energyEfficiencyData;
};
